/**
 * Appraisals — trade-in pipeline.
 *
 * Status chips (DRAFT → … → ACQUIRED/REJECTED), table with PPSR/recall
 * badges and cents-based valuations, a detail drawer with per-status
 * actions (checks, submit, valuation, approve/reject, convert to stock,
 * tender with bids + award) and a "New appraisal" modal.
 */
import { useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { appraisalsService } from '../lib/services';
import { useApi } from '../lib/hooks/useApi';
import { useToast } from '../lib/toast';
import { useCustomerOptions } from '../lib/useDirectory';
import LookupField from '../components/LookupField';

const STATUSES = ['DRAFT', 'PENDING_VALUATION', 'VALUED', 'APPROVED', 'TENDERED', 'ACQUIRED', 'REJECTED'];
const STATUS_TONE = {
  DRAFT: '', PENDING_VALUATION: 'info', VALUED: 'accent',
  APPROVED: 'success', TENDERED: 'warning', ACQUIRED: 'success', REJECTED: 'danger',
};
const PPSR_VAR = {
  CLEAR:       'var(--success)',
  ENCUMBERED:  'var(--danger)',
  WRITTEN_OFF: 'var(--danger)',
  STOLEN:      'var(--danger)',
  UNCHECKED:   'var(--text-tertiary)',
};
const RECALL_VAR = {
  NONE:      'var(--success)',
  CLEAR:     'var(--success)',
  OPEN:      'var(--danger)',
  UNCHECKED: 'var(--text-tertiary)',
};

function fmtMoney(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
}
function centsFromDollars(s) {
  if (s === '' || s == null) return null;
  const n = Number(String(s).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
function fmtWhen(iso) {
  return formatInOrgTz(iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function asList(d) {
  if (Array.isArray(d)) return d;
  return d?.content ?? [];
}

/* ── mocks ── */
const MOCK_APPRAISALS = [
  { id: 'apr-1', rego: 'ABC12D', regoState: 'NSW', vin: 'JTMWFREV60D012345', make: 'Mazda',   model: 'CX-5 Touring',   year: 2019, odometerKm: 78200, colour: 'Soul Red',   ppsrStatus: 'CLEAR',      recallStatus: 'NONE',      tradeValueCents: 2450000, retailValueCents: 2890000, status: 'VALUED',           customerId: 'cust-45', damageItems: [{ id: 'd1', area: 'Rear bumper', description: 'Scuff, respray required', severity: 'MINOR', estimatedCostCents: 45000 }] },
  { id: 'apr-2', rego: 'XYZ98K', regoState: 'NSW', vin: 'MR0FZ22G401234567', make: 'Toyota',  model: 'Hilux SR5',      year: 2021, odometerKm: 55100, colour: 'Glacier White', ppsrStatus: 'ENCUMBERED', recallStatus: 'NONE',      tradeValueCents: null,    retailValueCents: null,    status: 'PENDING_VALUATION', customerId: 'cust-77', damageItems: [] },
  { id: 'apr-3', rego: 'QWE45T', regoState: 'VIC', vin: null,                make: 'Hyundai', model: 'i30 N-Line',     year: 2020, odometerKm: 61400, colour: 'Polar White', ppsrStatus: 'UNCHECKED',  recallStatus: 'UNCHECKED', tradeValueCents: null,    retailValueCents: null,    status: 'DRAFT',            customerId: 'cust-90', damageItems: [{ id: 'd2', area: 'Windscreen', description: 'Chip, repairable', severity: 'MINOR', estimatedCostCents: 12000 }, { id: 'd3', area: 'Front left alloy', description: 'Kerb rash', severity: 'MINOR', estimatedCostCents: 18000 }] },
  { id: 'apr-4', rego: 'JKL33M', regoState: 'QLD', vin: 'KMHJ381GBLU123456', make: 'Kia',     model: 'Sorento Sport+', year: 2018, odometerKm: 98900, colour: 'Steel Grey', ppsrStatus: 'CLEAR',      recallStatus: 'OPEN',      tradeValueCents: 1980000, retailValueCents: 2440000, status: 'TENDERED',         customerId: 'cust-12', damageItems: [] },
  { id: 'apr-5', rego: 'MNO77P', regoState: 'NSW', vin: '6T153BEC10X123456', make: 'Toyota',  model: 'Corolla Ascent', year: 2022, odometerKm: 24300, colour: 'Silver',     ppsrStatus: 'CLEAR',      recallStatus: 'NONE',      tradeValueCents: 2210000, retailValueCents: 2590000, status: 'APPROVED',         customerId: 'cust-33', damageItems: [] },
  { id: 'apr-6', rego: 'RST11V', regoState: 'ACT', vin: 'WVWZZZAUZLW123456', make: 'VW',      model: 'Golf 110TSI',    year: 2017, odometerKm: 112000, colour: 'Tornado Red', ppsrStatus: 'WRITTEN_OFF', recallStatus: 'NONE',     tradeValueCents: null,    retailValueCents: null,    status: 'REJECTED',         customerId: 'cust-51', rejectReason: 'Written-off record on PPSR', damageItems: [] },
];
const MOCK_TENDER = {
  id: 'tender-1', appraisalId: 'apr-4', status: 'OPEN',
  closesAt: new Date(Date.now() + 2 * 86400e3).toISOString(),
  bids: [
    { id: 'bid-1', wholesalerName: 'Western Wholesale Cars', contact: 'dean@wwcars.com.au', amountCents: 1890000, placedAt: new Date(Date.now() - 5 * 3600e3).toISOString() },
    { id: 'bid-2', wholesalerName: 'AutoTrade Direct',       contact: '0412 555 021',       amountCents: 2015000, placedAt: new Date(Date.now() - 2 * 3600e3).toISOString() },
  ],
};

/* ── new appraisal modal ── */
function NewAppraisalModal({ open, onClose, onCreated }) {
  const { toast } = useToast();
  const { options: customerOptions, loading: customersLoading } = useCustomerOptions();
  const [form, setForm] = useState({
    customerId: '', rego: '', regoState: 'NSW', vin: '',
    make: '', model: '', year: '', odometerKm: '', colour: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!open) return null;
  const valid = form.rego.trim() && form.make.trim() && form.model.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await appraisalsService.create({
        customerId: form.customerId.trim() || null,
        rego: form.rego.trim().toUpperCase(),
        regoState: form.regoState,
        vin: form.vin.trim() || null,
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year === '' ? null : Number(form.year),
        odometerKm: form.odometerKm === '' ? null : Number(form.odometerKm),
        colour: form.colour.trim() || null,
      });
      toast('Appraisal created', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`Create failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(560px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">New appraisal</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div className="form-field">
              <label>Rego *</label>
              <input value={form.rego} onChange={(e) => set('rego', e.target.value.toUpperCase())} placeholder="ABC12D" autoFocus required />
            </div>
            <div className="form-field">
              <label>State</label>
              <select value={form.regoState} onChange={(e) => set('regoState', e.target.value)}>
                {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <LookupField
            id="apr-customer"
            label="Customer"
            placeholder="Search by name, phone or email…"
            emptyHint="No customer matches that search"
            options={customerOptions}
            loading={customersLoading}
            value={form.customerId || null}
            onChange={(id) => set('customerId', id || '')}
          />
          <div className="form-field">
            <label>VIN (optional)</label>
            <input value={form.vin} onChange={(e) => set('vin', e.target.value.toUpperCase())} maxLength={17} placeholder="17-char VIN" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div className="form-field">
              <label>Make *</label>
              <input value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="Mazda" required />
            </div>
            <div className="form-field">
              <label>Model *</label>
              <input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="CX-5 Touring" required />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <div className="form-field">
              <label>Year</label>
              <input value={form.year} onChange={(e) => set('year', e.target.value.replace(/[^\d]/g, ''))} maxLength={4} inputMode="numeric" placeholder="2019" />
            </div>
            <div className="form-field">
              <label>Odometer (km)</label>
              <input value={form.odometerKm} onChange={(e) => set('odometerKm', e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="78200" />
            </div>
            <div className="form-field">
              <label>Colour</label>
              <input value={form.colour} onChange={(e) => set('colour', e.target.value)} placeholder="Soul Red" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || !valid}>
            {busy ? 'Creating…' : 'Create appraisal'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── valuation modal ── */
function ValuationModal({ appraisal, onClose, onSaved }) {
  const { toast } = useToast();
  const [trade, setTrade] = useState('');
  const [retail, setRetail] = useState('');
  const [busy, setBusy] = useState(false);

  if (!appraisal) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await appraisalsService.setValuation(appraisal.id, {
        tradeCents: centsFromDollars(trade),
        retailCents: centsFromDollars(retail),
      });
      toast('Valuation recorded', { variant: 'success' });
      onSaved();
      onClose();
    } catch (err) {
      toast(`Valuation failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(420px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Enter valuation</div>
            <div className="mono text-xs text-muted">{appraisal.year} {appraisal.make} {appraisal.model} · {appraisal.rego}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-field">
            <label>Trade value (AUD) *</label>
            <input value={trade} onChange={(e) => setTrade(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="24500" autoFocus required />
          </div>
          <div className="form-field">
            <label>Retail value (AUD) *</label>
            <input value={retail} onChange={(e) => setRetail(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="28900" required />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || !trade || !retail}>
            {busy ? 'Saving…' : 'Save valuation'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── tender panel (inside drawer when status === TENDERED) ── */
function TenderPanel({ appraisal, onChanged }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [bid, setBid] = useState({ wholesalerName: '', contact: '', amount: '' });
  const setB = (k, v) => setBid((f) => ({ ...f, [k]: v }));

  const tenderQ = useApi(
    () => appraisalsService.tenders({ appraisalId: appraisal.id }),
    { fallback: MOCK_TENDER, deps: [appraisal.id] },
  );
  const raw = tenderQ.data;
  const tender = Array.isArray(raw) ? raw[0] : (raw?.content ? raw.content[0] : raw);
  const bids = tender?.bids ?? [];

  const addBid = async (e) => {
    e.preventDefault();
    if (!tender?.id || !bid.wholesalerName.trim()) return;
    setBusy(true);
    try {
      await appraisalsService.placeBid(tender.id, {
        wholesalerName: bid.wholesalerName.trim(),
        contact: bid.contact.trim() || null,
        amountCents: centsFromDollars(bid.amount),
      });
      toast('Bid recorded', { variant: 'success' });
      setBid({ wholesalerName: '', contact: '', amount: '' });
      tenderQ.refetch();
    } catch (err) {
      toast(`Bid failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const award = async (b) => {
    if (!tender?.id) return;
    setBusy(true);
    try {
      await appraisalsService.awardTender(tender.id, b.id);
      toast(`Tender awarded to ${b.wholesalerName}`, { variant: 'success' });
      tenderQ.refetch();
      onChanged();
    } catch (err) {
      toast(`Award failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>
        Tender
        {tender?.closesAt && (
          <span className="mono text-xs text-muted" style={{ marginLeft: 8 }}>
            closes {fmtWhen(tender.closesAt)}
          </span>
        )}
      </div>
      {bids.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 12 }}>No bids yet.</div>
      ) : (
        <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr><th>Wholesaler</th><th>Bid</th><th style={{ width: 80 }}></th></tr>
          </thead>
          <tbody>
            {[...bids].sort((a, b) => (b.amountCents ?? 0) - (a.amountCents ?? 0)).map((b) => (
              <tr key={b.id}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{b.wholesalerName}</div>
                  <div className="mono text-xs text-muted">{b.contact || '—'}</div>
                </td>
                <td style={{ fontWeight: 600 }}>{fmtMoney(b.amountCents)}</td>
                <td>
                  <button className="btn small primary" disabled={busy} onClick={() => award(b)}>Award</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <form onSubmit={addBid} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-field">
            <label>Wholesaler *</label>
            <input value={bid.wholesalerName} onChange={(e) => setB('wholesalerName', e.target.value)} placeholder="AutoTrade Direct" />
          </div>
          <div className="form-field">
            <label>Contact</label>
            <input value={bid.contact} onChange={(e) => setB('contact', e.target.value)} placeholder="phone / email" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div className="form-field" style={{ flex: 1 }}>
            <label>Amount (AUD) *</label>
            <input value={bid.amount} onChange={(e) => setB('amount', e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="20150" />
          </div>
          <button type="submit" className="btn small" disabled={busy || !bid.wholesalerName.trim() || !bid.amount}>+ Add bid</button>
        </div>
      </form>
    </div>
  );
}

/* ── drawer ── */
function AppraisalDrawer({ appraisal, onClose, onChanged }) {
  const { toast } = useToast();
  const { options: customerOptions } = useCustomerOptions();
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [tendering, setTendering] = useState(false);
  const [closesAt, setClosesAt] = useState('');

  const customerName = useMemo(() => {
    if (!appraisal) return null;
    if (appraisal.customerName) return appraisal.customerName;
    const hit = customerOptions.find(o => o.id === appraisal.customerId);
    return hit?.primary || null;
  }, [appraisal, customerOptions]);

  if (!appraisal) return null;

  const run = async (fn, okMsg, { close = true } = {}) => {
    setBusy(true);
    try {
      await fn();
      toast(okMsg, { variant: 'success' });
      onChanged();
      if (close) onClose();
    } catch (e) {
      toast(`Action failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
      <span className="text-muted" style={{ width: 110, flexShrink: 0 }}>{label}</span>
      <span>{value ?? '—'}</span>
    </div>
  );
  const damage = appraisal.damageItems ?? [];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }} />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 92vw)',
        background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-lg)', zIndex: 201, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {appraisal.year} {appraisal.make} {appraisal.model}
            </div>
            <div className="mono text-xs text-muted">{appraisal.rego} ({appraisal.regoState}) · {appraisal.id}</div>
          </div>
          <button className="btn small" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className={`pill filled ${STATUS_TONE[appraisal.status] || ''}`}>{(appraisal.status || '').replace(/_/g, ' ')}</span>
          <span className="pill" style={{ color: PPSR_VAR[appraisal.ppsrStatus || 'UNCHECKED'] }}>
            PPSR: {(appraisal.ppsrStatus || 'UNCHECKED').replace('_', ' ')}
          </span>
          <span className="pill" style={{ color: RECALL_VAR[appraisal.recallStatus || 'UNCHECKED'] }}>
            Recall: {(appraisal.recallStatus || 'UNCHECKED').replace('_', ' ')}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {row('VIN', appraisal.vin)}
          {row('Odometer', appraisal.odometerKm != null ? `${appraisal.odometerKm.toLocaleString('en-AU')} km` : '—')}
          {row('Colour', appraisal.colour)}
          {row('Customer', customerName || '—')}
          {row('Trade value', fmtMoney(appraisal.tradeValueCents))}
          {row('Retail value', fmtMoney(appraisal.retailValueCents))}
          {appraisal.rejectReason && row('Reject reason', appraisal.rejectReason)}
        </div>

        {/* Damage items */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Damage items ({damage.length})</div>
          {damage.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 12 }}>No damage recorded.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {damage.map((d) => (
                <div key={d.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontWeight: 600, flex: 1 }}>{d.area}</span>
                    <span className="pill">{d.severity || '—'}</span>
                    <span>{fmtMoney(d.estimatedCostCents)}</span>
                  </div>
                  <div className="text-muted">{d.description}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {appraisal.status === 'DRAFT' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn small"
                disabled={busy}
                onClick={() => run(() => appraisalsService.saveChecks(appraisal.id, { runPpsr: true, runRecall: true }), 'PPSR & recall checks run', { close: false })}
              >Run checks</button>
              <button
                className="btn small primary"
                disabled={busy}
                onClick={() => run(() => appraisalsService.submit(appraisal.id), 'Submitted for valuation')}
              >Submit for valuation</button>
            </div>
          )}

          {appraisal.status === 'VALUED' && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn small primary"
                  disabled={busy}
                  onClick={() => run(() => appraisalsService.approve(appraisal.id), 'Appraisal approved')}
                >Approve</button>
                <button className="btn small danger" disabled={busy} onClick={() => setRejecting((v) => !v)}>Reject…</button>
              </div>
              {rejecting && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div className="form-field" style={{ flex: 1 }}>
                    <label>Reject reason</label>
                    <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. reconditioning cost too high" />
                  </div>
                  <button
                    className="btn small danger"
                    disabled={busy || !rejectReason.trim()}
                    onClick={() => run(() => appraisalsService.reject(appraisal.id, rejectReason.trim()), 'Appraisal rejected')}
                  >Confirm reject</button>
                </div>
              )}
            </>
          )}

          {appraisal.status === 'APPROVED' && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn small primary"
                  disabled={busy}
                  onClick={() => run(() => appraisalsService.convertToStock(appraisal.id), 'Converted to stock')}
                >Convert to stock</button>
                <button className="btn small" disabled={busy} onClick={() => setTendering((v) => !v)}>Start tender…</button>
              </div>
              {tendering && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div className="form-field" style={{ flex: 1 }}>
                    <label>Tender closes at</label>
                    <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
                  </div>
                  <button
                    className="btn small primary"
                    disabled={busy || !closesAt}
                    onClick={() => run(
                      () => appraisalsService.sendToTender(appraisal.id, { closesAt: new Date(closesAt).toISOString() }),
                      'Tender opened',
                    )}
                  >Open tender</button>
                </div>
              )}
            </>
          )}

          {appraisal.status === 'TENDERED' && (
            <TenderPanel appraisal={appraisal} onChanged={onChanged} />
          )}

          {['PENDING_VALUATION'].includes(appraisal.status) && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              Awaiting manager valuation — use “Enter valuation” from the table row.
            </div>
          )}
          {['ACQUIRED', 'REJECTED'].includes(appraisal.status) && (
            <div className="text-muted" style={{ fontSize: 12 }}>This appraisal is closed — no further actions.</div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ── page ── */
export default function Appraisals() {
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [valuing, setValuing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, loading, refetch } = useApi(
    () => appraisalsService.list({ page: 0, limit: 100 }),
    { fallback: MOCK_APPRAISALS },
  );
  const appraisals = asList(data);

  const filtered = useMemo(
    () => appraisals.filter((a) => !statusFilter || a.status === statusFilter),
    [appraisals, statusFilter],
  );
  const countFor = (s) => appraisals.filter((a) => a.status === s).length;

  return (
    <>
      <TopBar
        crumbs={['Appraisals']}
        right={
          <>
            <button className="btn small" onClick={refetch}>↻ Refresh</button>
            <button className="btn small primary" onClick={() => setModalOpen(true)}>+ New appraisal</button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Pipeline chips */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            className={`pill ${statusFilter === '' ? 'active' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setStatusFilter('')}
          >All ({appraisals.length})</span>
          {STATUSES.map((s) => (
            <span
              key={s}
              className={`pill ${statusFilter === s ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter(s)}
            >{s.replace(/_/g, ' ')} ({countFor(s)})</span>
          ))}
          {loading && <span className="mono text-xs text-muted">loading…</span>}
        </div>

        {/* Table */}
        <div className="card" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: 40 }}>
              <div className="icon">🔍</div>
              <div className="title">No appraisals</div>
              <div className="desc">Start a trade-in appraisal with the button above.</div>
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rego / VIN</th><th>Vehicle</th><th>KM</th><th>PPSR</th><th>Recall</th>
                  <th>Trade</th><th>Retail</th><th>Status</th><th style={{ width: 130 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} onClick={() => setSelected(a)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="mono" style={{ fontWeight: 600 }}>{a.rego} <span className="text-muted">({a.regoState})</span></div>
                      <div className="mono text-xs text-muted">{a.vin || 'no VIN'}</div>
                    </td>
                    <td>{a.year} {a.make} {a.model}</td>
                    <td className="mono text-xs">{a.odometerKm != null ? a.odometerKm.toLocaleString('en-AU') : '—'}</td>
                    <td>
                      <span className="pill" style={{ color: PPSR_VAR[a.ppsrStatus || 'UNCHECKED'] }}>
                        {(a.ppsrStatus || 'UNCHECKED').replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className="pill" style={{ color: RECALL_VAR[a.recallStatus || 'UNCHECKED'] }}>
                        {(a.recallStatus || 'UNCHECKED').replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{fmtMoney(a.tradeValueCents)}</td>
                    <td>{fmtMoney(a.retailValueCents)}</td>
                    <td><span className={`pill filled ${STATUS_TONE[a.status] || ''}`}>{(a.status || '—').replace(/_/g, ' ')}</span></td>
                    <td>
                      {a.status === 'PENDING_VALUATION' && (
                        <button
                          className="btn small primary"
                          onClick={(e) => { e.stopPropagation(); setValuing(a); }}
                        >Enter valuation</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <AppraisalDrawer appraisal={selected} onClose={() => setSelected(null)} onChanged={refetch} />
      <ValuationModal appraisal={valuing} onClose={() => setValuing(null)} onSaved={refetch} />
      {/* Mounted only while open, so every "New appraisal" starts from a blank
          form — the modal keeps its fields in local state, which would otherwise
          survive the close and show the previous appraisal's details (DEF-171). */}
      {modalOpen && (
        <NewAppraisalModal open onClose={() => setModalOpen(false)} onCreated={refetch} />
      )}
    </>
  );
}
